import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import * as sessionService from '../../services/sessionService';

interface SessionForkButtonProps {
  sessionId: string;
}

export const SessionForkButton: React.FC<SessionForkButtonProps> = ({
  sessionId
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [forkName, setForkName] = useState('');
  const [forkDescription, setForkDescription] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const forkMutation = useMutation(
    (data: { name: string; description: string }) => 
      sessionService.forkSession(sessionId, data.name, data.description),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['sessions']);
        setIsModalOpen(false);
        navigate(`/sessions/${data.id}`);
      },
      onError: (error) => {
        console.error('Failed to fork session:', error);
        alert('Failed to fork session');
      }
    }
  );
  
  const handleFork = () => {
    if (!forkName.trim()) {
      alert('Please enter a name for the forked session');
      return;
    }
    
    forkMutation.mutate({ name: forkName, description: forkDescription });
  };
  
  return (
    <>
      <button
        className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center"
        onClick={() => setIsModalOpen(true)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v7h-2l-1 2H8l-1-2H5V5z" clipRule="evenodd" />
        </svg>
        Fork Session
      </button>
      
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">Fork Session</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={forkName}
                onChange={(e) => setForkName(e.target.value)}
                placeholder="Enter name for forked session"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={forkDescription}
                onChange={(e) => setForkDescription(e.target.value)}
                placeholder="Enter description (optional)"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                onClick={handleFork}
                disabled={forkMutation.isLoading}
              >
                {forkMutation.isLoading ? 'Forking...' : 'Fork Session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
